import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Question, Sparkle, Plus, X, Microphone, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(m, 1)}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export const MissingTermsCard = ({ onEdit, onAddManual, onChanged, refreshKey }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/kitchen-help/missing").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [refreshKey]);

  const addAi = async (m) => {
    setBusy(m.normalized);
    try {
      const { data } = await api.post("/admin/kitchen-help/missing/generate", { term: m.term });
      toast.success(data.already_existed ? `"${data.term.term}" already existed — marked as added` : `Added "${data.term.term}" — review and save`);
      onChanged();
      load();
      if (!data.already_existed) onEdit(data.term);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI couldn't add that term");
    } finally { setBusy(""); }
  };

  const dismiss = async (m) => {
    setItems((prev) => prev.filter((x) => x.normalized !== m.normalized));
    await api.post("/admin/kitchen-help/missing/dismiss", { normalized: m.normalized }).catch(() => load());
  };

  return (
    <div className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid="kh-missing-card">
      <div className="flex items-center gap-2 mb-1">
        <Question size={20} weight="duotone" className="text-rf-orange" />
        <h3 className="font-bold text-rf-ink">Asked but missing</h3>
        {items?.length > 0 && <span className="chip !bg-rf-orange/15 !text-rf-orange" data-testid="kh-missing-count">{items.length}</span>}
      </div>
      <p className="text-xs text-rf-ink-soft mb-3">Words cooks asked Voice (&ldquo;what is…&rdquo;) or typed into Kitchen Help search that found nothing.</p>
      {items === null ? (
        <p className="text-sm text-rf-ink-soft">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-rf-ink-soft" data-testid="kh-missing-empty">Nothing missing right now — nice.</p>
      ) : (
        <ul className="divide-y divide-rf-ink/5">
          {items.map((m) => (
            <li key={m.normalized} className="py-2 flex flex-wrap items-center gap-3" data-testid={`kh-missing-row-${m.normalized.replace(/\s+/g, "-")}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="font-bold text-rf-ink">{m.term}</p>
                <p className="text-xs text-rf-ink-soft inline-flex items-center gap-2">
                  Asked {m.count}× · {ago(m.last_at)}
                  {m.sources?.voice ? <span className="inline-flex items-center gap-0.5"><Microphone size={11} /> {m.sources.voice}</span> : null}
                  {m.sources?.search ? <span className="inline-flex items-center gap-0.5"><MagnifyingGlass size={11} /> {m.sources.search}</span> : null}
                </p>
              </div>
              <button onClick={() => addAi(m)} disabled={!!busy} data-testid="kh-missing-ai" className="inline-flex items-center gap-1.5 bg-rf-orange text-white px-3 py-1.5 rounded-full font-bold text-xs hover:bg-rf-ink disabled:opacity-50">
                <Sparkle size={12} /> {busy === m.normalized ? "Adding…" : "Add with AI"}
              </button>
              <button onClick={() => onAddManual(m.term)} data-testid="kh-missing-manual" className="inline-flex items-center gap-1.5 border border-rf-ink/15 px-3 py-1.5 rounded-full font-bold text-xs hover:bg-rf-ink hover:text-white">
                <Plus size={12} /> Add manually
              </button>
              <button onClick={() => dismiss(m)} aria-label={`Dismiss ${m.term}`} data-testid="kh-missing-dismiss" className="p-1 text-rf-ink-soft hover:text-red-600"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
